import { useForm } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';

import { useProjectTreeStore } from 'store/projectContract.store';

import { FormProps } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/CreateRiskRegisterModal/components/CreateRiskRegisterForm/CreateRiskRegisterForm';

export const useCreateRiskRegisterFormLogic = ({ api, ui, onClose }) => {
    const queryClient = useQueryClient();
    const location = useLocation();
    const { project, contract } = useProjectTreeStore();
    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const form = useForm<FormProps>({
        defaultValues: {
            contractId: contract?.id || 0,
            name: '',
            description: '',
            status: 'NEW',
            probability: 'HIGH',
            distribution: 'UNIFORM',
            distributionRange: {
                min: 0,
                middle: null,
                max: 100,
            },
            impactType: 'RELATIVE',
            assignee: 'Unassigned',
            assigneeValue: {
                assignee: {
                    id: null,
                    userName: 'Unassigned',
                    firstName: 'Unassigned',
                    lastName: '',
                    imageName: '',
                },
                id: null,
                label: `Unassigned`,
                value: 'Unassigned',
            },
            activitiesByKey: {},
            linkOption: 'all',
            ...(location.state as Partial<FormProps>),
        },
    });

    const handleFormSubmit = (data: FormProps) => {
        sendEventWithDimensions({ action: 'Create Risk', label: data.name, category: 'Risk Register' });
        const filters = [
            { data: [project?.id], name: 'PROJECT_CARD_BOARD' },
            { data: [contract?.id], name: 'CONTRACT_CARD_BOARD' },
        ];
        if (data.activitiesByKey?.wbs) {
            filters.push({
                data: [data.activitiesByKey.wbs],
                name: 'WBS_SEARCH',
            });
        }
        if (data.activitiesByKey?.activityCodes) {
            filters.push({
                data: data.activitiesByKey.activityCodes,
                name: 'ACTIVITY_CODES',
            });
        }
        if (data.activitiesByKey?.cluster) {
            filters.push({
                data: [data.activitiesByKey.cluster],
                name: 'CLUSTER_CARD_BOARD',
            });
        }
        if (data.activitiesByKey?.specific) {
            filters.push({
                data: data.activitiesByKey.specific,
                name: 'CUSTOM_ACTIVITIES_SET',
            });
        }

        const segmentId = data.activitiesByKey?.segment ?? null;

        const { activitiesByKey, assigneeValue, linkedActivities, ...payload } = data;

        api.createRisk.instance.mutate(
            {
                ...payload,
                assignee: data.assigneeValue?.value || '',
                segmentRequest: {
                    filter: {
                        filters,
                    },
                    segmentId,
                },
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries('getRiskRegister').then(() => {
                        handleSnackBar('Risk register created successfully', 'success');
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
                onSettled: () => {
                    onClose();
                    ui.setters.setShowConfirmModal(false);
                },
            },
        );
    };

    const closeModalConfirm = () => {
        if (form.formState.isDirty) {
            ui.setters.setShowConfirmModal(true);
        } else {
            onClose();
        }
        return false;
    };

    return { logic: { form, handleFormSubmit, closeModalConfirm } };
};
